'use client';

import { Text } from '@/design-system';
import AlertModalV2 from '../../Member/components/AlertModalV2';
import { SVG } from '../SVG';
import UImage from '../UImage';
import MarketingConsentCouponIssuedModal from './MarketingConsentCouponIssuedModal';
import {
  COUPON_AMOUNT_TEXT,
  MarketingConsentStep,
  useMarketingConsentModal,
} from './useMarketingConsentModal';

const BACKGROUND_IMAGE_URL = `${process.env.NEXT_PUBLIC_S3_IMAGE_URL}/assets/app-install-promotion/marketing_policy_background.png`;
const GRAPHIC_IMAGE_URL = `${process.env.NEXT_PUBLIC_S3_IMAGE_URL}/assets/app-install-promotion/marketing_policy_graphic.png`;

/**
 * 마케팅 수신 동의 유도 팝업 (1단계: 동의 유도 + 쿠폰 받기)
 *
 * 앱에서 마케팅 수신 동의율·푸시 도달율을 높이기 위한 팝업. 앱 전용 2,000원 쿠폰을 안내하고
 * "동의하고 쿠폰 받기" 클릭 시 발급 완료 바텀시트(MarketingConsentCouponIssuedModal)로 전환한다.
 * 노출 조건·단계 전환·재노출 억제·다른 자동 노출 모달과의 중첩 방지는 useMarketingConsentModal에서 담당한다.
 */
const MarketingConsentModal = () => {
  const {
    isOpen,
    step,
    issuedCoupon,
    isSubmitting,
    handleClickAgreeAndGetCoupon,
    handleClose,
    handleNeverShowForSevenDays,
    handleCloseCouponIssued,
    handleClickGoToMarket,
  } = useMarketingConsentModal();

  if (!isOpen) return null;

  if (step === MarketingConsentStep.COUPON_ISSUED) {
    return (
      <MarketingConsentCouponIssuedModal
        coupon={issuedCoupon}
        onClickGoToMarket={handleClickGoToMarket}
        onClose={handleCloseCouponIssued}
      />
    );
  }

  return (
    <AlertModalV2
      open={isOpen}
      onClose={handleClose}
      hasButtonGroup={false}
      hasCloseButton={false}
      zeroPadding
      StyledDialog={{ maxWidth: '343px', backgroundColor: 'transparent' }}
      content={
        <div className="relative flex w-full flex-col items-center justify-center gap-[20px] overflow-hidden rounded-[16px] bg-white px-[32px] pb-[24px] pt-[40px]">
          <UImage
            src={BACKGROUND_IMAGE_URL}
            alt=""
            width={288}
            height={360}
            className="absolute inset-0 h-full w-full object-cover"
          />
          <button
            onClick={handleClose}
            aria-label="닫기"
            className="absolute right-0 top-0 flex size-[56px] items-center justify-center"
            type="button"
          >
            <SVG.Close className="h-[24px] w-[24px] stroke-gray-900 stroke-[1.8]" />
          </button>
          <Text
            variant="h2-bold"
            as="p"
            className="relative text-center text-gray-900"
          >
            알림 받고 혜택 챙기세요
          </Text>
          <div className="relative flex w-full flex-col items-center gap-[12px]">
            <UImage
              src={GRAPHIC_IMAGE_URL}
              alt={`가장 빠른 특가 알림, 지금 먹어야 할 제철 알림, ${COUPON_AMOUNT_TEXT} 쿠폰 즉시 발급`}
              width={246}
              height={162}
            />
            <button
              onClick={handleClickAgreeAndGetCoupon}
              disabled={isSubmitting}
              className="flex h-[48px] w-full items-center justify-center rounded-[10px] bg-purple-500 disabled:opacity-70"
              type="button"
            >
              <Text variant="h5-bold-100" as="span" className="text-white">
                동의하고 쿠폰 받기
              </Text>
            </button>
            <button
              onClick={handleNeverShowForSevenDays}
              className="flex items-center"
              type="button"
            >
              <Text
                variant="h7-regular-100"
                as="span"
                className="text-gray-600"
              >
                7일 동안 보지 않기
              </Text>
            </button>
          </div>
        </div>
      }
    />
  );
};

export default MarketingConsentModal;
